import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import { RECURRENT_COMMENT_MAX, type RecurrentInstancesResult, type TaskCardType, type TaskType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import FormInput from 'components/forms/FormInput';
import SolarIcon from 'components/icons/SolarIcon';
import { useToast } from 'context/ToastContext';
import { getLocale } from 'utils/helpers/functions/getLocale';
import TaskStatusBadge from '../../shared/TaskStatusBadge';
import RecurrentSummary from '../../shared/recurrent/RecurrentSummary';

interface TaskCardRecurrentTabProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
    onHide: () => void;
    onOpenTask: (taskId: number) => void;
}

type SeriesAction = 'stop' | 'free';

interface MonthGroup {
    key: string;
    label: string;
    items: TaskType[];
}

const formatMonth = (millis: number, language: string): string => {
    const locale = getLocale(language);
    const dt = DT.fromMillis(millis, { zone: 'local' }).setLocale(locale);
    if (!dt.isValid) return '';
    const label = dt.toFormat('LLLL yyyy');
    return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
};

const groupByMonth = (tasks: TaskType[], language: string, noDateLabel: string): MonthGroup[] => {
    const groups: MonthGroup[] = [];
    tasks.forEach((item) => {
        const millis = Number(item.dateEnd || 0);
        const dt = millis ? DT.fromMillis(millis, { zone: 'local' }) : null;
        const key = dt?.isValid ? dt.toFormat('yyyy-LL') : 'none';
        const label = dt?.isValid ? formatMonth(millis, language) : noDateLabel;
        const current = groups.find((group) => group.key === key);
        if (current) current.items.push(item);
        else groups.push({ key, label, items: [item] });
    });
    return groups;
};

const TaskCardRecurrentTab = ({ task, onTaskUpdated, onHide, onOpenTask }: TaskCardRecurrentTabProps) => {
    const { t, i18n } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const [series, setSeries] = useState<RecurrentInstancesResult | null>(null);
    const [action, setAction] = useState<SeriesAction | null>(null);

    const load = async () => {
        const result = await apiTask.getRecurrentInstances(task.ID);
        setSeries(result);
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task.ID, task.recurrentActive, task.recurrentCancel]);

    const groups = groupByMonth(series?.tasks || [], i18n.language, t('recurrent.noDate'));
    const canStop = Boolean(task.recurrentActive) && !task.recurrentCancel;

    const run = async (nextAction: SeriesAction, comment: string): Promise<boolean> => {
        const text = comment.trim();
        if (!text) {
            showToast(t('recurrent.errComment'), 'danger');
            return false;
        }
        if (text.length > RECURRENT_COMMENT_MAX) {
            showToast(t('recurrent.errCommentLong'), 'danger');
            return false;
        }
        const result =
            nextAction === 'stop'
                ? await apiTask.stopRecurrentSeries(task.ID, text)
                : await apiTask.deleteFreeRecurrentTasks(task.ID, text);
        if (!result?.stat) {
            const code = result?.errCode;
            if (code === 'NO_FREE_TASKS') showToast(t('recurrent.errNoFree'), 'warning');
            else if (code === 'SERIES_INACTIVE') showToast(t('recurrent.errInactive'), 'warning');
            else if (code === 'SERIES_DENIED') showToast(t('recurrent.errDenied'), 'danger');
            else showToast(t('common:messError'), 'danger');
            return false;
        }
        setAction(null);
        if (result.closed || !result.task) {
            onHide();
            return true;
        }
        onTaskUpdated(result.task);
        showToast(nextAction === 'stop' ? t('recurrent.messStopped') : t('recurrent.messDeletedFree'), 'success');
        return true;
    };

    const toggleAction = (next: SeriesAction) => {
        setAction((current) => (current === next ? null : next));
    };

    return (
        <div>
            <section className="task-card-section mb-3">
                <div className="task-card-section__label">
                    <SolarIcon name="bootstrap-reboot" className="me-1" />
                    {t('tabRecurrent')}
                </div>
                <RecurrentSummary
                    data={task.recurrentData || null}
                    allowOverlap={Boolean(task.recurrentAllowOverlap)}
                    active={Boolean(task.recurrentActive)}
                    cancelled={Boolean(task.recurrentCancel)}
                    showAllowOverlap={Boolean(task.reserveExecutorTime)}
                    hiddenCount={Number(series?.hiddenCount || 0)}
                />
            </section>

            {series?.canManage ? (
                <div className="d-flex flex-column align-items-start gap-1 mb-3">
                    {canStop ? (
                        <button
                            type="button"
                            className="btn btn-link p-0 align-baseline fs-12"
                            onClick={() => toggleAction('stop')}
                        >
                            <SolarIcon name="pause-circle" className="me-1 fs-14" />
                            {t('recurrent.stop')}
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline fs-12 text-danger"
                        onClick={() => toggleAction('free')}
                    >
                        <SolarIcon name="trash" className="me-1 fs-14" />
                        {t('recurrent.deleteFree')}
                    </button>
                </div>
            ) : null}

            {action ? (
                <div className="task-card-panel mb-3">
                    <FormInput
                        key={action}
                        compact
                        title={t('recurrent.comment')}
                        showForm
                        setShowForm={(show) => {
                            if (!show) setAction(null);
                        }}
                        as="textarea"
                        rows={3}
                        required
                        requiredMessage={t('recurrent.errComment')}
                        maxLength={RECURRENT_COMMENT_MAX}
                        submitLabel={action === 'stop' ? t('recurrent.stop') : t('recurrent.deleteFree')}
                        callBack={(value) => run(action, String(value))}
                    />
                </div>
            ) : null}

            {groups.length === 0 ? (
                <div className="task-card-empty">{t('recurrent.noInstances')}</div>
            ) : (
                groups.map((group) => (
                    <section key={group.key} className="task-card-section mb-3">
                        <div className="task-card-section__label">{group.label}</div>
                        <div className="task-card-panel task-recurrent-list">
                            {group.items.map((item) => (
                                <button
                                    key={item.ID}
                                    type="button"
                                    className={`task-recurrent-list__row text-body${item.ID === task.ID ? ' task-recurrent-list__row--current' : ''}`}
                                    onClick={() => onOpenTask(item.ID)}
                                >
                                    <span className="text-body">
                                        {item.dateEnd
                                            ? DT.fromMillis(item.dateEnd, { zone: 'local' }).toFormat('dd.MM.yyyy HH:mm')
                                            : '—'}
                                    </span>
                                    <TaskStatusBadge
                                        status={item.taskStatus}
                                        dateEnd={item.dateEnd}
                                        taskDeleted={item.taskDeleted}
                                    />
                                </button>
                            ))}
                        </div>
                    </section>
                ))
            )}
        </div>
    );
};

export default TaskCardRecurrentTab;
